import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Sparkles } from "lucide-react";
import { FloatingHearts } from "./FloatingHearts";
import { DECK, POSITIONS, CARD_BACK, shuffle } from "../data/deck";

export const CardDraw = ({ t, lang, onReveal }) => {
  const pool = useMemo(() => shuffle(DECK).slice(0, 21), []);
  const [drawn, setDrawn] = useState([]); // array of card objects
  const drawnIds = new Set(drawn.map((c) => c.id));

  const handlePick = (card) => {
    if (drawnIds.has(card.id) || drawn.length >= 9) return;
    setDrawn([...drawn, card]);
  };

  const nameOf = (c) => (lang === "de" ? c.name_de : c.name_en);
  const posOf = (i) => (lang === "de" ? POSITIONS[i].de : POSITIONS[i].en);

  return (
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <FloatingHearts count={10} />
      <div className="relative z-10">
      <div className="text-center mb-8">
        <h2 className="font-serif-display text-4xl sm:text-5xl rose-gold-text glow-rose mb-3">{t.draw_title}</h2>
        <p className="text-muted-foreground max-w-xl mx-auto">{t.draw_sub}</p>
        <p className="mt-4 text-sm uppercase tracking-[0.2em] text-primary/90" data-testid="draw-progress">
          {t.draw_progress(drawn.length)}
        </p>
      </div>

      {/* Curved fan of face-down cards */}
      <div className="w-full overflow-hidden flex justify-center mb-12">
        <div
          className="relative origin-center scale-[0.56] sm:scale-90 lg:scale-100"
          style={{ width: 600, height: 210 }}
        >
          {pool.map((card, idx) => {
            const picked = drawnIds.has(card.id);
            const mid = (pool.length - 1) / 2;
            const off = idx - mid;
            const angle = off * 3.0;
            const x = off * 26;
            const dip = Math.pow(Math.abs(off), 2) * 0.55;
            const disabled = picked || drawn.length >= 9;
            return (
              <div
                key={card.id}
                className="absolute"
                style={{ left: `calc(50% + ${x}px)`, bottom: `${66 - dip}px`, transform: "translateX(-50%)" }}
              >
                <motion.button
                  data-testid={`pool-card-${card.id}`}
                  onClick={() => handlePick(card)}
                  disabled={disabled}
                  initial={false}
                  animate={{ rotate: angle, opacity: picked ? 0.15 : 1, y: picked ? -34 : 0 }}
                  whileHover={!disabled ? { y: -30, scale: 1.1, zIndex: 60 } : {}}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="block w-[62px] rounded-lg overflow-hidden border border-primary/40 shadow-[0_6px_18px_rgba(0,0,0,0.6)] hover:border-primary disabled:cursor-default"
                  style={{ aspectRatio: "0.67", transformOrigin: "bottom center" }}
                >
                  <img src={CARD_BACK} alt="" className="w-full h-full object-cover" />
                </motion.button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 9 spread slots */}
      <div className="grid grid-cols-3 gap-3 sm:gap-5 max-w-3xl mx-auto">
        {POSITIONS.map((_, i) => {
          const card = drawn[i];
          return (
            <div key={i} className="flex flex-col items-center">
              <div
                className={`relative w-full rounded-xl overflow-hidden ${card ? "velvet-rose card-glow" : "border border-dashed border-border bg-card/40"}`}
                style={{ aspectRatio: "0.67", perspective: 1200 }}
                data-testid={`spread-slot-${i + 1}`}
              >
                <AnimatePresence>
                  {card && (
                    <motion.div
                      key={card.id}
                      initial={{ rotateY: 180, opacity: 0 }}
                      animate={{ rotateY: 0, opacity: 1 }}
                      transition={{ duration: 0.6 }}
                      className="absolute inset-0"
                    >
                      <img src={card.img} alt={nameOf(card)} className="w-full h-full object-cover" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
                        <p className="text-[10px] sm:text-xs text-center text-primary-foreground/90 font-medium leading-tight">
                          {nameOf(card)}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {!card && (
                  <div className="absolute inset-0 flex items-center justify-center text-2xl text-muted-foreground/40 font-serif-display">
                    {i + 1}
                  </div>
                )}
              </div>
              <p className="mt-2 text-[11px] sm:text-xs text-center text-muted-foreground leading-tight">
                {posOf(i)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-12">
        <Button
          data-testid="reveal-btn"
          disabled={drawn.length < 9}
          onClick={() => onReveal(drawn)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-base disabled:opacity-40 shadow-[0_0_30px_rgba(212,175,55,0.3)]"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {t.reveal_btn}
        </Button>
      </div>
      </div>
    </div>
  );
};
